import React from "react";
import { Text } from "../../components";
import { stateOptions } from "../commonStateTaxData";
import { AMOUNT_REGEX_STR } from "../helpers";

export const basicInfoQuestions = [
  {
    id: "employee_first_name",
    title: "First name",
    is_required: true,
    question_type: "short_text",
  },
  {
    id: "employee_middle_name",
    title: "Middle initial",
    question_type: "short_text",
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^[a-zA-Z]{1}$',
        customErrorMessage: 'The middle initial can only contain 1 letter.',
      },
    ],
  },
  {
    id: "employee_last_name",
    title: "Last name",
    is_required: true,
    question_type: "short_text",
  },
  {
    id: "employee_ssn",
    title: "Social Security number",
    is_required: true,
    placeholder: "9 digits",
    question_type: "identity",
    identity_type: "ssn",
    rules: [
      {
        category: "regex",
        action: "match",
        value: "^\\d{9}$",
      },
    ],
  },
  {
    id: "street_address",
    title: "Permanent address",
    is_required: true,
    question_type: "short_text",
  },
  {
    id: "apt_num",
    title: "Apartment Number",
    question_type: "short_text",
  },
  {
    id: "city_or_town",
    title: "City or town",
    is_required: true,
    question_type: "short_text",
  },
  {
    id: "state",
    title: "State",
    is_required: true,
    question_type: "dropdown",
    options: stateOptions,
  },
  {
    id: "zip_code",
    title: "Zip code",
    is_required: true,
    question_type: "short_text",
    placeholder: "5 digits",
    rules: [
      {
        category: "regex",
        action: "match",
        value: "^\\d{5}$",
      },
    ],
  },
  {
    id: "filing_status",
    title: "Please select filing status:",
    is_required: true,
    question_type: "multiple_choice",
    options: [
      {
        codeValue: "single_or_head_of_household",
        shortName: "Single or Head of household",
      },
      {
        codeValue: "married",
        shortName: "Married",
      },
      {
        codeValue: "married_at_single_rate",
        shortName: "Married, but withhold at higher single rate",
      },
    ],
  },
];

export const residencyQuestions = [
  {
    id: "resident_of_nyc",
    title: "Are you a resident of New York City",
    is_required: true,
    question_type: "multiple_choice",
    options: [
      {
        codeValue: "true",
        shortName: "Yes",
      },
      {
        codeValue: "false",
        shortName: "No",
      },
    ],
  },
  {
    id: "resident_of_yonkers",
    title: "Are you a resident of Yonkers?",
    is_required: true,
    question_type: "multiple_choice",
    options: [
      {
        codeValue: "true",
        shortName: "Yes",
      },
      {
        codeValue: "false",
        shortName: "No",
      },
    ],
  },
];

export const getResidencyQuestions = (version) => {
  let newYorkCityTitle = "Are you a resident of New York City?";
  if (["2024", "2025"].includes(version)) newYorkCityTitle = "Are you a resident of New York City (this includes the Bronx, Brooklyn, Manahttan, Queens, and Staten Island)?";

  return [
    {
      id: "resident_of_nyc",
      title: newYorkCityTitle,
      is_required: true,
      question_type: "multiple_choice",
      options: [
        {
          codeValue: "true",
          shortName: "Yes",
        },
        {
          codeValue: "false",
          shortName: "No",
        },
      ],
    },
    {
      id: "resident_of_yonkers",
      title: "Are you a resident of Yonkers?",
      is_required: true,
      question_type: "multiple_choice",
      options: [
        {
          codeValue: "true",
          shortName: "Yes",
        },
        {
          codeValue: "false",
          shortName: "No",
        },
      ],
    },
  ];
};

export const dependentsQuestion = [
  {
    id: "num_dependents",
    title:
      "Enter the number of dependents that you will claim on your state return (do not include yourself or, if married, your spouse).",
    is_required: true,
    question_type: "short_text",
    rules: [
      {
        category: "regex",
        action: "match",
        value: "^(0|[1-9][0-9]*)$",
      },
      {
        value: "100",
        action: "lte",
        category: "number",
      },
    ],
  },
];

export const claimCreditQuestions = [
  {
    id: "credit_claimed",
    title: "Select each credit you expect to claim on your tax return.",
    is_required: false,
    question_type: "multiple_selection",
    options: [
      { codeValue: "college_tuition", shortName: "College tuition credit" },
      {
        codeValue: "nys_household",
        shortName: "New York State household credit",
      },
      { codeValue: "property_tax", shortName: "Real property tax credit" },
      {
        codeValue: "child_dependent_care",
        shortName: "Child and dependent care credit",
      },
      { codeValue: "earned_income", shortName: "Earned income credit" },
      {
        codeValue: "empire_state_child",
        shortName: "Empire State child credit",
      },
      {
        codeValue: "nyc_school_tax",
        shortName: "New York City school tax credit",
      },
      {
        codeValue: "head_of_household_status",
        shortName: "Head of household status and only one job",
      },
    ],
  },
];

export const otherCreditsQuestion = [
  {
    id: "other_credits",
    title:
      "Enter other credits (see page 2 of New York IT-2014 for instructions)",
    is_required: false,
    question_type: "short_text",
    rules: [
      {
        category: "regex",
        action: "match",
        value: "^[0-9]+(.[0-9]{1,2})?$",
      },
    ],
  },
  {
    id: "federal_adjustments",
    title:
      "Enter an estimate of your federal adjustments to income, such as deductible IRA contributions you make for the tax year. Total estimate.",
    is_required: false,
    question_type: "short_text",
    rules: [
      {
        category: "regex",
        action: "match",
        value: "^[0-9]+(.[0-9]{1,2})?$",
      },
    ],
  },
];

export const itemizedDeductionQuestions = [
  {
    id: "itemized_deductions_value",
    title:
      "Enter your estimated NY itemized deductions for the tax year (see Form IT-196 and its instructions; enter the amount from line 49).",
    question_type: "short_text",
    is_required: true,
    rules: [
      {
        category: "regex",
        action: "match",
        value: "^[0-9]+(.[0-9]{1,2})?$",
      },
    ],
  },
  {
    id: "status_amt",
    title:
      "Based on your federal filing status, select the applicable status and amount.",
    question_type: "dropdown",
    is_required: true,
    options: [
      {
        codeValue: "single_not_dependent",
        shortName: "Single (cannot be claimed as a dependent) - $8,000",
      },
      {
        codeValue: "single_dependent",
        shortName: "Single (can be claimed as a dependent) - $3,100",
      },
      {
        codeValue: "head_of_household_amt",
        shortName: "Head of household - $11,200",
      },
      { codeValue: "widow", shortName: "Qualifying widow(er) - $16,050" },
      {
        codeValue: "married_joint",
        shortName: "Married filing jointly - $16,050",
      },
      {
        codeValue: "married_separate",
        shortName: "Married filing separate returns - $8,000",
      },
    ],
  },
];

export const coveredEmployeeQuestion = [
  {
    id: "expected_wages",
    title:
      "Expected annual wages and compensation from electing employer in 2021.",
    question_type: "short_text",
    is_required: true,
    rules: [
      {
        category: "regex",
        action: "match",
        value: "^[0-9]+(.[0-9]{1,2})?$",
      },
    ],
  },
];

export const additionalWithholdingQuestions = [
  {
    id: "ny_additional_amount",
    title: "New York State amount",
    question_type: "short_text",
    rules: [
      {
        category: "regex",
        action: "match",
        value: "^[0-9]+(.[0-9]{1,2})?$",
      },
    ],
  },
  {
    id: "nyc_additional_amount",
    title: "New York City amount",
    question_type: "short_text",
    rules: [
      {
        category: "regex",
        action: "match",
        value: "^[0-9]+(.[0-9]{1,2})?$",
      },
    ],
  },
  {
    id: "yonkers_additional_amount",
    title: "Yonkers amount",
    question_type: "short_text",
    rules: [
      {
        category: "regex",
        action: "match",
        value: "^[0-9]+(.[0-9]{1,2})?$",
      },
    ],
  },
];

const worksheet = (
  <a
    href="https://www.tax.ny.gov/forms/current-forms/it/it2104i.htm"
    target="_blank"
  >
    worksheet
  </a>
);

export const totalAllowancesQuestions = [
  {
    id: "total_num_allowances_nys_yonkers",
    title: (
      <Text>
        Total number of allowances you are claiming for New York State and
        Yonkers, if applicable (from line 19, if using {worksheet})
      </Text>
    ),
    question_type: "short_text",
    rules: [
      {
        category: "regex",
        action: "match",
        value: AMOUNT_REGEX_STR,
      },
    ],
  },
  {
    id: "total_num_allowances_nyc",
    title: (
      <Text>
        Total number of allowances for New York City (from line 31, if using{" "}
        {worksheet})
      </Text>
    ),
    question_type: "short_text",
    rules: [
      {
        category: "regex",
        action: "match",
        value: AMOUNT_REGEX_STR,
      },
    ],
  },
];
